/**
 * Metadados VISUAIS dos nós do Comercial (leads, empresas, atividades e notas)
 * nas automações (issue #155).
 *
 * Os campos e rótulos vêm do catálogo do backend
 * (api/automations/comercial_leads_nodes.py); aqui ficam só o ícone/tom e o
 * resumo de uma linha do card. Separado de nodeMeta.js (que importa este
 * arquivo) para não misturar com os demais módulos.
 */

export const COMERCIAL_LEADS_NODE_META = {
  'trigger.lead_updated': { icon: 'edit' },
  'trigger.lead_status_changed': { icon: 'status' },
  'trigger.lead_merged': { icon: 'users' },
  'trigger.lead_linked_to_company': { icon: 'link' },
  'trigger.lead_channel_added': { icon: 'phone' },
  'trigger.lead_no_contact': { icon: 'clock' },
  'trigger.lead_no_owner': { icon: 'user' },
  'trigger.company_created': { icon: 'briefcase' },
  'trigger.company_imported': { icon: 'download' },
  'trigger.company_updated': { icon: 'edit' },
  'trigger.activity_updated': { icon: 'edit' },
  'trigger.activity_deadline_changed': { icon: 'calendar' },
  'trigger.activity_reassigned': { icon: 'user' },
  'trigger.activity_due_soon': { icon: 'bell' },
  'trigger.note_added': { icon: 'page' },
  'action.create_lead': { icon: 'leads' },
  'action.create_company': { icon: 'briefcase' },
  'action.link_lead_to_company': { icon: 'link' },
  'action.update_company_field': { icon: 'edit' },
  'action.merge_leads': { icon: 'users' },
  'action.add_lead_channel': { icon: 'phone' },
  'action.archive_lead': { icon: 'inbox', tone: 'slate' },
  'action.import_company': { icon: 'download' },
  'action.enrich_company': { icon: 'sparkle' },
  'action.update_activity': { icon: 'edit' },
  'action.reschedule_activity': { icon: 'refresh' },
  'action.delete_activity': { icon: 'trash', tone: 'slate' },
}

const trunc = (s, n = 40) => (s && s.length > n ? `${s.slice(0, n - 1)}…` : s || '')

const STATUS = {
  prospect: 'prospect', lead: 'lead', negociacao: 'negociação', cliente: 'cliente',
  recorrente: 'recorrente', churned: 'churned', arquivado: 'arquivado',
}
const CAMPO_LEAD = {
  nome: 'nome', data_nascimento: 'nascimento', origem: 'origem', status: 'status', company_id: 'empresa',
  owner_id: 'responsável', whatsapp_id: 'WhatsApp', faturamento: 'faturamento', empresa: 'empresa (texto)',
  valor_venda: 'valor de venda',
}
const CAMPO_EMPRESA = {
  nome: 'nome', razao_social: 'razão social', nome_fantasia: 'nome fantasia', cnpj: 'CNPJ', dominio: 'domínio',
  segmento: 'segmento', tamanho: 'tamanho', cidade: 'cidade', estado: 'estado', status: 'status',
  porte: 'porte', situacao_cadastral: 'situação cadastral',
}
const CAMPO_ATIVIDADE = {
  titulo: 'título', descricao: 'descrição', tipo: 'tipo', prioridade: 'prioridade',
  contato: 'lead', negocio: 'negócio',
}
const CANAL = { phone: 'telefone', email: 'e-mail', linkedin: 'LinkedIn', url: 'site' }
const FONTE = { manual: 'cadastro manual', cnpj_lookup: 'base de CNPJ', import: 'importação', apollo: 'Apollo', scraping: 'scraping' }
const ANTES = { 15: '15 min', 30: '30 min', 60: '1 hora', 120: '2 horas', 1440: '1 dia' }
const UNIDADE = { minutes: 'min', hours: 'h', days: 'dia(s)' }
const BUSCA = { cnpj: 'pelo CNPJ', nome: 'pelo nome' }
const DUPLICADO = { phone: 'telefone', email: 'e-mail', phone_or_email: 'telefone ou e-mail' }
const CAMPO_UPDATE_EMPRESA = {
  nome: 'nome', dominio: 'domínio', segmento: 'segmento', tamanho: 'tamanho',
  cidade: 'cidade', estado: 'estado', status: 'status',
}

/** Sufixo de filtro de tipo de atividade: " · ligação". */
const tipo = (config) => (config.tipo ? ` · ${config.tipo}` : '')

/**
 * Resumo de uma linha dos nós do Comercial. Devolve `undefined` para tipos que
 * não são do Comercial (quem chama segue para o resumo dos demais nós).
 */
export function summarizeComercialLeads(key, config = {}, names = {}) {
  const nome = (id) => names[id] || null
  switch (key) {
    case 'trigger.lead_updated':
      return config.field ? `Mudou ${CAMPO_LEAD[config.field] || config.field}` : 'Qualquer dado do lead'
    case 'trigger.lead_status_changed': {
      if (config.to_status && config.from_status) {
        return `${STATUS[config.from_status] || config.from_status} → ${STATUS[config.to_status] || config.to_status}`
      }
      if (config.to_status) return `Passou para ${STATUS[config.to_status] || config.to_status}`
      if (config.from_status) return `Saiu de ${STATUS[config.from_status] || config.from_status}`
      return 'Qualquer mudança de status'
    }
    case 'trigger.lead_merged':
      return 'Lead fundido com outro'
    case 'trigger.lead_linked_to_company':
      return 'Lead vinculado a uma empresa'
    case 'trigger.lead_channel_added':
      return config.channel_type ? `Novo ${CANAL[config.channel_type] || config.channel_type}` : 'Qualquer canal adicionado'
    case 'trigger.lead_no_contact':
      return config.days
        ? `Sem contato há ${config.days} dia(s)${config.status ? ` · ${STATUS[config.status] || config.status}` : ''}`
        : 'Informe os dias sem contato'
    case 'trigger.lead_no_owner':
      return config.hours
        ? `Sem responsável há ${config.hours} h${config.status ? ` · ${STATUS[config.status] || config.status}` : ''}`
        : 'Informe as horas sem responsável'
    case 'trigger.company_created':
      return config.fonte ? `Empresa nova (${FONTE[config.fonte] || config.fonte})` : 'Qualquer empresa nova'
    case 'trigger.company_imported':
      return 'Empresa importada (CNPJ ou importação)'
    case 'trigger.company_updated':
      return config.field ? `Mudou ${CAMPO_EMPRESA[config.field] || config.field}` : 'Qualquer dado da empresa'
    case 'trigger.activity_updated':
      return config.field ? `Mudou ${CAMPO_ATIVIDADE[config.field] || config.field}${tipo(config)}` : `Atividade atualizada${tipo(config)}`
    case 'trigger.activity_deadline_changed':
      return `Prazo alterado${tipo(config)}`
    case 'trigger.activity_reassigned':
      return config.user_id
        ? `Passou para ${nome(config.user_id) || 'pessoa selecionada'}${tipo(config)}`
        : `Responsável alterado${tipo(config)}`
    case 'trigger.activity_due_soon':
      return config.minutes
        ? `${ANTES[config.minutes] || `${config.minutes} min`} antes do vencimento${tipo(config)}`
        : 'Escolha quanto tempo antes'
    case 'trigger.note_added':
      return 'Qualquer nota nova'
    case 'action.create_lead':
      return config.nome ? `Cria ${trunc(config.nome, 36)}` : 'Defina o nome do lead'
    case 'action.create_company':
      return config.nome ? `Cria ${trunc(config.nome, 36)}` : 'Defina o nome da empresa'
    case 'action.link_lead_to_company':
      return config.by && config.by !== 'flow'
        ? `Empresa ${BUSCA[config.by]}${config.value ? `: ${trunc(config.value, 28)}` : ''}`
        : 'Vincula à empresa do fluxo'
    case 'action.update_company_field':
      return config.field && config.value
        ? `${CAMPO_UPDATE_EMPRESA[config.field] || config.field} = ${trunc(config.value, 32)}`
        : 'Escolha o campo e o valor'
    case 'action.merge_leads':
      return `Funde duplicados por ${DUPLICADO[config.match || 'phone_or_email'] || 'telefone ou e-mail'}`
    case 'action.add_lead_channel':
      return config.channel_type && config.value
        ? `${CANAL[config.channel_type] || config.channel_type}: ${trunc(config.value, 32)}`
        : 'Escolha o canal e o valor'
    case 'action.archive_lead':
      return 'Arquiva o lead'
    case 'action.import_company':
      return config.cnpj ? `CNPJ ${trunc(config.cnpj, 24)}` : 'Informe o CNPJ'
    case 'action.enrich_company':
      return 'Atualiza com a base de CNPJ'
    case 'action.update_activity': {
      const campos = ['titulo', 'descricao', 'tipo', 'prioridade', 'user_id']
        .filter((c) => config[c]).map((c) => (c === 'user_id' ? 'responsável' : CAMPO_ATIVIDADE[c]))
      return campos.length ? `Altera: ${campos.join(', ')}` : 'Escolha o que alterar'
    }
    case 'action.reschedule_activity': {
      if (config.mode === 'fixed') {
        return config.date ? `Para ${trunc(config.date, 16)}${config.hora ? ` ${config.hora}` : ''}` : 'Defina a nova data'
      }
      if (config.mode === 'shift' && config.amount) {
        return `${config.direction === 'earlier' ? 'Adianta' : 'Adia'} ${config.amount} ${UNIDADE[config.unit || 'hours']}`
      }
      return 'Escolha como reagendar'
    }
    case 'action.delete_activity':
      return 'Exclui a atividade'
    default:
      return undefined
  }
}
